import { LockOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card, DatePicker, Select, Skeleton, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { TiendaPerfectaDistributor } from 'models/TiendaPerfecta';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import { formatInstant } from '../utils/time';

const I18N_BASE = 'scenes.route_report.components.filters';

interface RouteReportFiltersProps {
  date: Dayjs;
  onDateChange: (value: Dayjs) => void;
  distributors: TiendaPerfectaDistributor[];
  selectedDistributorId: string | undefined;
  onDistributorChange: (id: string | undefined) => void;
  singleDistributor: boolean;
  disabled?: boolean;
  loading?: boolean;
  routeCount: number;
  /** Un refresco en curso: gira el icono, no bloquea la vista. */
  refreshing?: boolean;
  onRefresh: () => void;
  lastUpdatedAt: Date | null;
  userTimeZone: string;
}

function RouteReportFilters({
  date,
  onDateChange,
  distributors,
  selectedDistributorId,
  onDistributorChange,
  singleDistributor,
  disabled = false,
  loading = false,
  routeCount,
  refreshing = false,
  onRefresh,
  lastUpdatedAt,
  userTimeZone,
}: RouteReportFiltersProps) {
  const { t } = useTranslation();

  // Solo se bloquea cuando no hay nada que elegir: un rol `low` alcanza un único
  // distribuidor, así que el selector lo muestra pero no lo deja cambiar.
  const distributorDisabled = disabled || singleDistributor;

  return (
    <Card>
      <div className="route-report-filters">
        <div className="route-report-filters__left">
          <span className="route-report-filters__label">
            {t(`${I18N_BASE}.date`)}
          </span>

          <DatePicker
            value={date}
            onChange={(value) => value && onDateChange(value)}
            allowClear={false}
            disabled={disabled}
            format="DD/MM/YYYY"
            // Un día futuro no es un error para el contrato, pero no tiene
            // ejecución que reportar: no se ofrece.
            disabledDate={(current) =>
              current && current > dayjs().endOf('day')
            }
          />

          <span className="route-report-filters__label route-report-filters__label--spaced">
            {t(`${I18N_BASE}.distributor`)}
          </span>

          <Select
            value={selectedDistributorId}
            onChange={onDistributorChange}
            // Deshabilitado pero visible: esconderlo dejaría la vista sin decir
            // de qué distribuidores son las rutas.
            disabled={distributorDisabled}
            suffixIcon={distributorDisabled ? <LockOutlined /> : undefined}
            placeholder={t(`${I18N_BASE}.all_distributors`)}
            allowClear={!singleDistributor}
            options={distributors.map((distributor) => ({
              value: distributor.id,
              label: distributor.tradeName,
              title: `${distributor.tradeName} ${distributor.code}`,
            }))}
            showSearch
            filterOption={(search: string, option?: any) =>
              FunctionsHelper.normalizeText(option?.title ?? '').includes(
                FunctionsHelper.normalizeText(search)
              )
            }
            style={{ minWidth: 220 }}
          />
        </div>

        <div className="route-report-filters__right">
          {loading ? (
            <Skeleton.Input active size="small" style={{ width: 104 }} />
          ) : (
            <span>{t(`${I18N_BASE}.summary`, { count: routeCount })}</span>
          )}

          {/* La hora del dato, no la del reloj: si un refresco falla deja de
              avanzar, y esa es la señal de que lo que se ve quedó viejo. */}
          {lastUpdatedAt && (
            <span className="route-report-filters__updated">
              {t(`${I18N_BASE}.updated_at`, {
                time: formatInstant(lastUpdatedAt, userTimeZone),
              })}
            </span>
          )}

          <Tooltip title={t(`${I18N_BASE}.refresh`)}>
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={onRefresh}
              disabled={disabled || loading}
              aria-label={t(`${I18N_BASE}.refresh`)}
            />
          </Tooltip>
        </div>
      </div>
    </Card>
  );
}

export default RouteReportFilters;
